import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export const CART_KEY = "kaakazini_cart";
export const BUY_NOW_KEY = "kaakazini_buy_now";

// ── Helpers ─────────────────────────────────────────────────────────────────

export const makeCartItemId = (id, size, color) =>
  [id ?? "", size || "", color || ""]
    .map(String)
    .join("|")
    .toLowerCase();

const toStock = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export function normalizeItem(raw) {
  if (!raw || typeof raw !== "object") return null;

  const id = raw.id ?? raw.django_id;
  if (id === undefined || id === null || id === "") return null;

  const price = Number(raw.price);
  const qty = Math.floor(Number(raw.qty ?? raw.quantity ?? 1));
  const selectedSize = raw.selectedSize || null;
  const selectedColor = raw.selectedColor || null;

  return {
    id,
    cartItemId: raw.cartItemId || makeCartItemId(id, selectedSize, selectedColor),
    title: raw.title || raw.name || "Product",
    price: Number.isFinite(price) && price >= 0 ? price : 0,
    qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
    image_url: raw.image_url || raw.image || null,
    selectedSize,
    selectedColor,
    stock: toStock(raw.stock),
    category: raw.category || "",
    vendor: raw.vendor || raw.seller_name || "",
  };
}

function readCart() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CART_KEY) || "[]");
    return (Array.isArray(parsed) ? parsed : [])
      .map(normalizeItem)
      .filter(Boolean);
  } catch (error) {
    console.error("Unable to read cart:", error);
    return [];
  }
}

function writeCart(items) {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  } catch (error) {
    console.error("Unable to save cart:", error);
  }
}

// ── "Buy now" item (a single product that skips the cart) ───────────────────
// Kept in sessionStorage so a stale buy-now item never lingers for days.

export function saveBuyNowItem(item) {
  try {
    sessionStorage.setItem(BUY_NOW_KEY, JSON.stringify(item));
    return true;
  } catch (error) {
    console.error("Unable to start buy-now checkout:", error);
    return false;
  }
}

export function readBuyNowItem() {
  try {
    const raw = sessionStorage.getItem(BUY_NOW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return normalizeItem(Array.isArray(parsed) ? parsed[0] : parsed);
  } catch (error) {
    return null;
  }
}

export function clearBuyNowItem() {
  try {
    sessionStorage.removeItem(BUY_NOW_KEY);
  } catch (error) {
    /* nothing to do */
  }
}

// ── Context ─────────────────────────────────────────────────────────────────

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(readCart);
  const [isOpen, setIsOpen] = useState(false);

  // Always holds the latest items so quick double-clicks can't race.
  const itemsRef = useRef(items);

  const commit = useCallback((next) => {
    itemsRef.current = next;
    setItems(next);
    writeCart(next);
  }, []);

  // Keep several browser tabs in sync.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === CART_KEY || event.key === null) {
        const next = readCart();
        itemsRef.current = next;
        setItems(next);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Returns { ok, message } so screens can show what happened.
  const addToCart = useCallback(
    (input) => {
      const item = normalizeItem(input);
      if (!item) {
        return { ok: false, message: "This product could not be added." };
      }

      const current = itemsRef.current;
      const index = current.findIndex((i) => i.cartItemId === item.cartItemId);
      const inCart = index >= 0 ? current[index].qty : 0;
      const nextQty = inCart + item.qty;

      if (item.stock !== null && nextQty > item.stock) {
        const left = Math.max(0, item.stock - inCart);
        return {
          ok: false,
          message:
            left === 0
              ? `All ${item.stock} available are already in your cart.`
              : `Only ${item.stock} available. You can add ${left} more.`,
        };
      }

      const next =
        index >= 0
          ? current.map((i, n) =>
              n === index ? { ...i, qty: nextQty, stock: item.stock } : i
            )
          : [...current, item];

      commit(next);

      return {
        ok: true,
        message: `${item.qty} ${item.qty === 1 ? "item" : "items"} added to your cart.`,
      };
    },
    [commit]
  );

  const removeFromCart = useCallback(
    (cartItemId) => {
      commit(itemsRef.current.filter((i) => i.cartItemId !== cartItemId));
    },
    [commit]
  );

  const updateQty = useCallback(
    (cartItemId, qty) => {
      const target = Math.floor(Number(qty));
      if (!Number.isFinite(target)) return { ok: false, message: "Invalid quantity." };

      const current = itemsRef.current;
      const item = current.find((i) => i.cartItemId === cartItemId);
      if (!item) return { ok: false, message: "Item not found." };

      if (target < 1) {
        commit(current.filter((i) => i.cartItemId !== cartItemId));
        return { ok: true, message: "" };
      }

      if (item.stock !== null && target > item.stock) {
        return {
          ok: false,
          message: `Only ${item.stock} available for ${item.title}.`,
        };
      }

      commit(
        current.map((i) =>
          i.cartItemId === cartItemId ? { ...i, qty: target } : i
        )
      );
      return { ok: true, message: "" };
    },
    [commit]
  );

  const clearCart = useCallback(() => commit([]), [commit]);

  const totalItems = useMemo(
    () => items.reduce((sum, i) => sum + i.qty, 0),
    [items]
  );
  const totalPrice = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.qty, 0),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      isOpen,
      setIsOpen,
      addToCart,
      removeFromCart,
      updateQty,
      clearCart,
      totalItems,
      totalPrice,
    }),
    [items, isOpen, addToCart, removeFromCart, updateQty, clearCart, totalItems, totalPrice]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used inside <CartProvider>. Wrap your app in it (see App.jsx).");
  }
  return ctx;
}
